import { CheckCircle, Warning } from '@phosphor-icons/react';
import {
  getListApprovalsQueryKey,
  getReviewSummaryQueryKey,
  useDecideApproval,
} from '@reelplicate/api-client/approvals';
import type { ReviewDraftDto } from '@reelplicate/api-client/model';
import { Badge, Button, Inline, Link, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { humanize } from '@/features/flows/catalogue-ui';
import { canRewrite, RewriteLine } from '@/features/flows/editor/run/rewrite-line';

/**
 * Figma "Home / Draft card" (133:5): a draft awaiting review - 9:16
 * thumbnail with the hook over a dark gradient, title, flow/format, the
 * guardrail result, then Approve / Open. A draft a guardrail held shows why
 * in a warning box and can't be approved until its line is rewritten.
 */
export function DraftCard({
  workspaceId,
  draft,
}: {
  workspaceId: string;
  draft: ReviewDraftDto;
}) {
  const { item } = draft;
  const held = draft.kind === 'HELD';
  const reasons = item.reason?.split(' · ') ?? [];
  const queryClient = useQueryClient();
  const decide = useDecideApproval();
  const [rewriting, setRewriting] = useState(false);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getReviewSummaryQueryKey(workspaceId) }),
      queryClient.invalidateQueries({ queryKey: getListApprovalsQueryKey(workspaceId) }),
    ]);

  const approve = async () => {
    if (!draft.approvalId) return;
    try {
      await decide.mutateAsync({
        id: workspaceId,
        approvalId: draft.approvalId,
        data: { decision: 'APPROVE' },
      });
    } catch {
      return; // Shown as a toast.
    }
    toast.success('Draft approved');
    await refresh();
  };

  return (
    <article
      aria-label={item.summary}
      className="bg-bg-surface border-border-subtle flex min-w-0 flex-1 gap-4 rounded-xl border p-3"
    >
      {draft.approvalId && draft.item.media.some((file) => file.kind === 'image') ? (
        // Opens the draft large in the review queue, slide by slide.
        <RouterLink
          to={`/flows/drafts?view=${draft.approvalId}`}
          aria-label={`Open ${draft.item.summary}`}
          className="focus-visible:shadow-focus-ring shrink-0 cursor-zoom-in rounded-lg outline-none"
        >
          <Thumbnail draft={draft} />
        </RouterLink>
      ) : (
        <Thumbnail draft={draft} />
      )}

      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div className="flex flex-col gap-1.5 pb-3">
          <h3 className="text-body-md-strong text-text-primary">{item.summary}</h3>
          <p className="text-caption text-text-secondary flex items-center gap-1.5">
            {draft.flowName} · {humanize(item.dataType)}
            {draft.runMode === 'TEST' && <Badge size="sm">Test run</Badge>}
          </p>
          {held ? (
            <>
              <p className="text-caption text-status-warning-fg flex items-center gap-1.5">
                <Warning aria-hidden className="size-3.5 shrink-0" />
                {reasons.length === 1
                  ? '1 line needs a look'
                  : `${reasons.length} lines need a look`}
              </p>
              <div className="bg-status-warning-bg border-status-warning-border flex flex-col gap-1.5 rounded-md border px-3 py-2.5">
                <p className="text-caption text-text-primary">{item.reason}</p>
                {rewriting ? (
                  <RewriteLine
                    workspaceId={workspaceId}
                    item={item}
                    onCancel={() => setRewriting(false)}
                    onRevised={() => void refresh()}
                  />
                ) : (
                  canRewrite(item) && (
                    <Link asChild size="sm" className="self-start">
                      <button type="button" onClick={() => setRewriting(true)}>
                        Rewrite line
                      </button>
                    </Link>
                  )
                )}
              </div>
            </>
          ) : (
            draft.claimsChecked && (
              <p className="text-caption text-status-success-fg flex items-center gap-1.5">
                <CheckCircle aria-hidden weight="fill" className="size-3.5 shrink-0" />
                All claims approved
              </p>
            )
          )}
        </div>
        {/* Side by side when there's room; stacked in a narrow card. */}
        <Inline gap={2} wrap>
          <Button
            size="sm"
            className="flex-1 basis-22"
            disabled={held}
            loading={decide.isPending}
            onClick={() => void approve()}
          >
            Approve
          </Button>
          <Button asChild size="sm" variant="outline" className="flex-1 basis-22">
            {/* Held drafts live on their run's canvas; queued ones in Drafts. */}
            <RouterLink
              to={held ? `/flows/${draft.flowId}` : `/flows/drafts?run=${draft.runId}`}
            >
              Open
            </RouterLink>
          </Button>
        </Inline>
      </div>
    </article>
  );
}

/** The first frame of a reel, a carousel's first slide, or just the hook for a script. */
function Thumbnail({ draft }: { draft: ReviewDraftDto }) {
  const { item } = draft;
  const [file] = item.media;
  const script = (
    typeof item.payload.script === 'object' && item.payload.script
      ? item.payload.script
      : item.payload
  ) as Record<string, unknown>;
  const hook = typeof script.hook === 'string' ? script.hook : null;
  const seconds =
    typeof item.payload.durationSeconds === 'number'
      ? item.payload.durationSeconds
      : null;

  return (
    <div className="bg-bg-inverse relative flex min-h-[170px] w-24 shrink-0 flex-col justify-between overflow-hidden rounded-lg p-2">
      {file?.kind === 'image' && (
        <img src={file.url} alt="" className="absolute inset-0 size-full object-cover" />
      )}
      {file?.kind === 'video' && (
        <video
          src={file.url}
          aria-hidden
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 size-full object-cover"
        />
      )}
      <div
        aria-hidden
        className="absolute inset-0 bg-linear-to-b from-[rgb(38_39_25/0)] from-20% via-[rgb(38_39_25/0.55)] via-60% to-[rgb(38_39_25/0.92)]"
      />
      {seconds !== null ? (
        <span className="bg-bg-inverse text-caption text-text-inverse relative self-start rounded-full px-1.5 py-0.5">
          <span className="sr-only">Duration </span>
          {formatDuration(seconds)}
        </span>
      ) : (
        <span />
      )}
      {hook && <p className="text-body-sm-strong text-text-inverse relative">{hook}</p>}
    </div>
  );
}

/** 24.4 → "0:24". */
function formatDuration(seconds: number) {
  const total = Math.round(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
